import EulaAgreement from '@/components/Eula/EulaAgreement';
import ErrorBoundary from '@/components/Reusable/ErrorHandling/ErrorBoundary';
import ErrorComponent from '@/components/Reusable/ErrorHandling/ErrorComponent';
import SnackBarAlert from '@/components/Reusable/GeneralUse/SnackBarAlert';
import Layout from '@/ui/Layout';
import { fontClassnamesGlobal } from '@/ui/theme/fonts/nextFonts';
import { HubPrimaryColorProvider } from '@/ui/theme/ThemeContext';
import { setCallback } from '@/utils/auth/callbacks';
import {
  clearLocalCognitoSession,
  hubAmplifySignOut,
} from '@/utils/auth/clearMfaSessionSatisfaction';
import { axiosRequestNeedsLiveIdToken } from '@/utils/auth/axiosRequestNeedsLiveIdToken';
import {
  attachCognitoSessionKeepAlive,
  clearClientIdTokenCache,
  getClientIdToken,
} from '@/utils/auth/getClientIdToken';
import { SessionProvider } from '@/utils/auth/SessionContext';
import {
  checkSessionLimit,
  DEFAULT_SESSION_LIMIT_VALUE,
  removeSessionTimer,
  startSessionTimer,
} from '@/utils/auth/sessionLimit';
import { getCurrentUser, Hub } from '@/utils/aws/cognito/amplifyAuth';
import { configureAmplify } from '@/utils/aws/cognito/config';
import interceptConsole, {
  setClientLoggingAuthState,
} from '@/utils/client/logging';
import { IS_ETS_NEXT } from '@/utils/constants';
import { SnackBarContext } from '@/utils/snackbar';
import { CacheProvider } from '@emotion/react';
import '@fontsource/public-sans';
import '@fontsource/public-sans/500.css';
import { Button, CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { useRouter } from 'next/router';
import nProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IdleTimerProvider } from 'react-idle-timer';
import '../components/PageSpecific/metrics/D3/index.css';
import '../components/PageSpecific/travel-authorization/logs/index.css';
import createHubTheme from '../styles/createHubTheme';
import '../styles/fonts.css';
import createEmotionCache from '../utils/createEmotionCache';
configureAmplify();

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY);

const queryClient = new QueryClient();

const clientSideEmotionCache = createEmotionCache();

/** Throttle for cross-tab idle timer sync (react-idle-timer recommended default). */
const IDLE_TIMER_CROSS_TAB_SYNC_MS = 200;

function App({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps: {
    session: propsSession,
    hubUiTranslations,
    hubUiLocale,
    hubUiTranslationScopeKey,
    ...pageProps
  },
}) {
  useEffect(() => {
    setClientLoggingAuthState(Boolean(propsSession?.user));
  }, [propsSession?.user]);

  useEffect(() => {
    interceptConsole();
  }, []);

  // Attach a live idToken to API Gateway and same-origin /api requests.
  useEffect(() => {
    const invokeUrl = process.env.NEXT_PUBLIC_INVOKE_URL;
    const interceptorId = axios.interceptors.request.use(
      async (config) => {
        const url = config.url ?? config.baseURL ?? '';
        if (!axiosRequestNeedsLiveIdToken(url, invokeUrl)) return config;
        const token = await getClientIdToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
        }
        return config;
      },
      (err) => Promise.reject(err)
    );
    return () => axios.interceptors.request.eject(interceptorId);
  }, []);

  // Configure routing.
  useEffect(() => {
    nProgress.configure({
      parent: '#nprogress-container',
    });
    router.events.on('routeChangeStart', nProgress.start);
    router.events.on('routeChangeComplete', nProgress.done);
    router.events.on('routeChangeError', nProgress.done);
    return () => {
      router.events.off('routeChangeStart');
      router.events.off('routeChangeComplete');
      router.events.off('routeChangeError');
    };
  }, []);

  // Impersonation headers stay on defaults. Authorization is attached per
  // request from getClientIdToken so keep-alive rotations are not overwritten
  // by the GSSP token.
  useEffect(() => {
    delete axios.defaults.headers.common.Authorization;
    if (propsSession?.isImpersonating) {
      axios.defaults.headers.common.impuser = propsSession.user.email;
      axios.defaults.headers.common.imporg = propsSession.org.name;
    }
  });

  const router = useRouter();
  // Refresh Amplify cookies before ID-token expiry so GSSP stays on JWT verify
  // instead of Cognito + PreTokenGeneration. Keep-alive force-refreshes Cognito
  // when the cached ID token is within 120s of expiry. Idle timeout / session cap still apply.
  useEffect(() => {
    if (!propsSession?.user) {
      return undefined;
    }
    return attachCognitoSessionKeepAlive(router);
  }, [propsSession?.user, router]);

  const [openSnack, setOpenSnack] = useState(false);
  const [alert, setAlert] = useState(undefined);
  const [snackPack, setSnackPack] = useState([]);
  const updateSnack = useCallback((alertObj) => {
    setSnackPack((prev) => [
      ...prev,
      { ...alertObj, key: new Date().getTime() },
    ]);
  }, []);
  const snackBarContextValue = useMemo(() => ({ updateSnack }), [updateSnack]);

  // Create theme.
  const themeColor = propsSession?.org?.themeColor || '#040059';
  const theme = createHubTheme(themeColor);

  function onIdleHandler(_event, idleTimer) {
    // In-memory API Gateway token cache; safe to drop immediately (no server round-trip).
    clearClientIdTokenCache();
    setOpenSnack(false);
    setAlert(undefined);
    setSnackPack([]);
    // With crossTab + leaderElection, only the leader tab performs sign-out and activity logging. Prevents duplicate logs.
    if (idleTimer?.isLeader && !idleTimer.isLeader()) {
      clearLocalCognitoSession().then(() => {
        setCallback(router.asPath);
        router.push('/login');
      });
      return;
    }
    // MFA clear uses the Hub session cookie; run it before Cognito sign-out tears the session down.
    // clearMfaSessionSatisfaction resolves even when the POST fails, so sign-out still runs afterward.
    hubAmplifySignOut({
      reason: 'idle-timeout',
      userEmail: propsSession?.user?.email,
    }).then(() => {
      setCallback(router.asPath);
      router.push('/login');
    });
  }

  function onIdlePromptHandler(_, { activate }) {
    getCurrentUser()
      .then(() =>
        updateSnack({
          action: (
            <Button
              variant='contained'
              onClick={() => {
                activate();
                setOpenSnack(false);
              }}
              size='small'
              color='success'
            >
              Extend
            </Button>
          ),
          message:
            'Your session will expire in 1 minute. Extend the session by clicking the button below',
          autoHideDuration: 61 * 1000, // Make it so the snack never disappears before the user times out.
          disableWindowBlurListener: true,
        })
      )
      .catch(() => activate());
  }

  // Amplify v6 Hub auth events (v5 used signIn / signOut / oAuthSignOut).
  // Primary start path for password/MFA; SSO also starts explicitly on /sso-finish.
  // signedOut is a backup clear — clearLocalCognitoSession already removes the timer.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn') {
        startSessionTimer();
      } else if (event === 'signedOut') {
        clearClientIdTokenCache();
        removeSessionTimer();
      }
    });
    return unsubscribe;
  }, []);

  // Poll wall-clock Session Limit every 15s. Ref keeps the org threshold current across
  // client navigations (empty-deps closure would freeze first-render propsSession).
  const sessionLimitRef = useRef(
    propsSession?.org?.sessionLimit || DEFAULT_SESSION_LIMIT_VALUE
  );
  sessionLimitRef.current =
    propsSession?.org?.sessionLimit || DEFAULT_SESSION_LIMIT_VALUE;

  useEffect(() => {
    let timeoutId;
    const tick = () => {
      checkSessionLimit(sessionLimitRef.current, {
        router,
        updateSnack,
      });
      timeoutId = setTimeout(tick, 15 * 1000);
    };
    tick();
    return () => clearTimeout(timeoutId);
  }, [router, updateSnack]);

  // Prevent rendering of layout on 404 page. 404 pages are required to be static, and
  // thus they cannot use getServerSideProps. See
  // https://nextjs.org/docs/messages/404-get-initial-props for more info.
  if (router.route === '/404') {
    return <Component {...pageProps} />;
  }

  return (
    <CacheProvider value={emotionCache}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <HubPrimaryColorProvider color={propsSession?.org?.themeColor}>
            <CssBaseline />
            <SessionProvider session={propsSession}>
              <IdleTimerProvider
                timeout={(propsSession?.org?.sessionTimeout || 30) * 60 * 1000}
                promptBeforeIdle={60 * 1000}
                crossTab
                syncTimers={IDLE_TIMER_CROSS_TAB_SYNC_MS}
                leaderElection
                onIdle={onIdleHandler}
                onPrompt={onIdlePromptHandler}
              >
                <QueryClientProvider client={queryClient}>
                  <SnackBarContext.Provider value={snackBarContextValue}>
                    <Layout
                      fontClassnames={fontClassnamesGlobal}
                      hubUiTranslations={hubUiTranslations}
                      hubUiLocale={hubUiLocale}
                      hubUiTranslationScopeKey={hubUiTranslationScopeKey}
                    >
                      <ErrorBoundary type='page'>
                        {pageProps.error ? (
                          <ErrorComponent message={pageProps.error} />
                        ) : (
                          <Component {...pageProps} />
                        )}
                        {!IS_ETS_NEXT && <EulaAgreement />}
                        <SnackBarAlert
                          openSnack={openSnack}
                          setOpenSnack={setOpenSnack}
                          alert={alert}
                          setAlert={setAlert}
                          snackPack={snackPack}
                          setSnackPack={setSnackPack}
                        />
                      </ErrorBoundary>
                    </Layout>
                  </SnackBarContext.Provider>
                </QueryClientProvider>
              </IdleTimerProvider>
            </SessionProvider>
          </HubPrimaryColorProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  );
}

export default App;

App.getInitialProps = async (appContext) => {
  let pageProps = {};

  if (appContext.Component.getInitialProps) {
    pageProps = await appContext.Component.getInitialProps(appContext.ctx);
  }

  let hubUiTranslations = null;
  let hubUiLocale = null;
  let hubUiTranslationScopeKey = null;

  if (
    typeof window === 'undefined' &&
    appContext.ctx.req &&
    appContext.ctx.res
  ) {
    try {
      const pathname =
        appContext.ctx.pathname ?? appContext.router?.pathname ?? '/';
      // Runtime guard: only call on SSR. Client resolution in Turbopack still analyzes this
      // import — next.config aliases it to the .browser stub (no SQL / tls).
      const { loadHubUiBundleForRequest } = await import(
        '@/utils/i18n/server/loadHubUiBundleForRequest'
      );
      const result = await loadHubUiBundleForRequest(appContext.ctx, pathname);
      if (result) {
        hubUiTranslations = result.translations;
        hubUiLocale = result.locale;
        hubUiTranslationScopeKey = result.scopeKey;
      }
    } catch {
      // Client loadBundle will retry if SSR translation load fails.
    }
  }

  return {
    pageProps: {
      ...pageProps,
      hubUiTranslations,
      hubUiLocale,
      hubUiTranslationScopeKey,
    },
  };
};
